import type { TableSort } from '@shared/components/data-table/data-table';
import { Component, ChangeDetectionStrategy, OnInit, signal, inject, computed, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Address } from '@shared/models/address.model';
import { AddressService } from '@shared/services/address.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideRocket } from '@ng-icons/lucide';
import { AddressTable } from '@shared/components/address-table/address-table';
import { CreateCampaignDialog } from '@features/campaigns/components/create-campaign-dialog/create-campaign-dialog';
import { Campaign } from '@features/campaigns/models/campaign.model';
import { InlineNotice } from '@shared/components/inline-notice/inline-notice';

@Component({
  selector: 'app-addresses-page',
  standalone: true,
  imports: [
    CommonModule,
    HlmButtonDirective,
    NgIconComponent,
    AddressTable,
    CreateCampaignDialog,
    InlineNotice,
  ],
  providers: [provideIcons({ lucideRocket })],
  templateUrl: './addresses-page.html',
  styleUrl: './addresses-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressesPage implements OnInit {
  private readonly addressService = inject(AddressService);
  readonly currentOrgStore = inject(CurrentOrganizationStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  campaignDialog = viewChild<CreateCampaignDialog>('campaignDialog');
  addressTable = viewChild<AddressTable>('addressTable');

  addresses = signal<Address[]>([]);
  isLoading = signal(false);
  error = signal<string | null>(null);
  currentPage = signal(1);
  totalPages = signal(0);
  totalAddresses = signal(0);
  selectedAddressIds = signal<string[]>([]);
  isSelectingAll = signal(false);

  readonly selectedAddresses = computed(() =>
    this.addresses().filter((a) => this.selectedAddressIds().includes(a.addressId))
  );

  private readonly PAGE_SIZE = 20;
  private sort: TableSort | null = null;
  private loadVersion = 0;

  // Get current organization
  currentOrganization = computed(() => this.currentOrgStore.organization());
  organizationId = computed(
    () => this.currentOrgStore.organization()?.id || ''
  );

  async ngOnInit(): Promise<void> {
    await this.loadAddresses();
  }

  async loadAddresses(): Promise<void> {
    const organization = this.currentOrganization();
    if (!organization) return;

    const version = ++this.loadVersion;
    this.isLoading.set(true);
    this.error.set(null);

    try {
      const result = await this.addressService.getPaginatedAddresses(
        organization.id,
        this.currentPage(),
        this.PAGE_SIZE,
        this.sort,
      );
      if (version !== this.loadVersion) return;
      this.addresses.set(result.data);
      this.totalPages.set(result.meta.pages);
      this.totalAddresses.set(result.meta.total);
    } catch (err) {
      if (version !== this.loadVersion) return;
      this.error.set(err instanceof Error ? err.message : 'Fehler beim Laden der Adressen');
    } finally {
      if (version === this.loadVersion) this.isLoading.set(false);
    }
  }

  async onPageChange(page: number): Promise<void> {
    this.currentPage.set(page);
    await this.loadAddresses();
  }

  async onSortChange(sort: TableSort | null): Promise<void> {
    this.sort = sort;
    this.currentPage.set(1);
    await this.loadAddresses();
  }

  onSelectionChanged(ids: string[]): void {
    this.selectedAddressIds.set(ids);
  }

  async onRequestSelectAll(): Promise<void> {
    const organization = this.currentOrganization();
    if (!organization || this.isSelectingAll()) return;
    this.isSelectingAll.set(true);
    try {
      const result = await this.addressService.getPaginatedAddresses(
        organization.id,
        1,
        this.totalAddresses(),
        this.sort,
      );
      this.addressTable()?.selectAllAddresses(result.data);
      this.selectedAddressIds.set(result.data.map((a) => a.addressId));
    } catch {
      // Silently ignore — user can still work with what's selected
    } finally {
      this.isSelectingAll.set(false);
    }
  }

  onCreateCampaignRequested(addresses: Address[]): void {
    this.campaignDialog()?.openWithPreselectedAddresses(addresses);
  }

  onCreateCampaignFromSelection(): void {
    const selected = this.addressTable()?.selectedAddressObjects() ?? this.selectedAddresses();
    this.campaignDialog()?.openWithPreselectedAddresses(selected);
  }

  onCampaignCreated(campaign: Campaign): void {
    void this.router.navigate(['/campaigns', campaign.id]);
  }

  async onAddressCreated(_address: Address): Promise<void> {
    this.currentPage.set(1);
    await this.loadAddresses();
  }

  async onAddressUpdated(_address: Address): Promise<void> {
    await this.loadAddresses();
  }
}
